import { ApiResponse } from '../../@types/ApiResponse';
import { OrgUser } from '../../@types/ApiResponse/OrgUser';
import { ACT, FUNCTION_SET } from '../../constants';
import { getTempInfo } from '../../shared/tempInfo';
import { isEmpty } from 'lodash';
import { isNil } from 'ramda';

export const ClientList = {
  web: 'web',
  wxApplet: 'wxApplet',
  ios: 'ios',
} as const;
type ClientType = typeof ClientList[keyof typeof ClientList];

/**
 * 获取空间client标识客户端类型
 * 用于判断针对指定客户端的隐藏节点
 */
export const getOrgClient = (orgId: string, client: ClientType) => {
  return orgId.toUpperCase() === '3A0328AC0FA311EB9A4A7CD30AB8A430' ? '' : client;
};

// 节点是否已删除
export const isDelete = (node: PiNodeMetaData) => {
  return node.f === 2 || node.f === -1;
};

// 节点是否隐藏
export const isHide = (
  node: PiNodeMetaData,
  rootNode: PiNodeMetaData,
  client: ClientType | '',
  metadataMap: Record<string, PiNodeMetaData>,
  parentChildrenMap: Record<string, Record<string, boolean>>,
  wildNode: PiNodeManager['_wildNode'],
  orgInfo: ApiResponse.CurrentUser.OrgInfo,
) => {
  if (node.n === orgInfo.toBeSortId) {
    if ((rootNode.e._sys_function & FUNCTION_SET.CLEARING) === 0) {
      return true;
    }
  }

  let ret = !!(node.iH || `_sys_${client}_hide` in node.e);
  if (!ret && orgInfo.role === 7 && '_sys_collection' in node.e) {
    let f = true;
    const children = getChildrenByMetadata(
      node.n,
      metadataMap,
      parentChildrenMap,
      wildNode,
      orgInfo,
      false,
    );
    for (let i = 0; i < children.length; i++) {
      children[i].t;
      const childIsHide = isHide(
        children[i],
        rootNode,
        client,
        metadataMap,
        parentChildrenMap,
        wildNode,
        orgInfo,
      );
      if (!childIsHide) {
        f = false;
        break;
      }
    }
    return f;
  }
  return ret;
};

type getChildrenByMetadataReturnType<U extends boolean> = U extends true
  ? PiNodeMetaDataWithTempInfo[]
  : PiNodeMetaData[];

// 通过metadata获取子节点
export const getChildrenByMetadata = <U extends boolean>(
  id: string,
  metadataMap: Record<string, PiNodeMetaData>,
  parentChildrenMap: PiNodeManager['_parentIndex'],
  wildNode: PiNodeManager['_wildNode'],
  orgInfo: ApiResponse.CurrentUser.OrgInfo,
  withTempInfo: U,
): getChildrenByMetadataReturnType<U> => {
  const node = metadataMap[id];
  if (!node) {
    return [];
  }
  let childMap = parentChildrenMap[id];

  if (id === orgInfo.rootId) {
    childMap = { ...childMap, ...wildNode };
  }

  if (!childMap || Object.keys(childMap).length === 0) {
    return [];
  }

  const childs: getChildrenByMetadataReturnType<U> = [];
  Object.keys(childMap).map((key) => {
    let child = metadataMap[key];
    if (child) {
      if (withTempInfo) {
        (child as PiNodeMetaDataWithTempInfo) = {
          ...child,
          tempInfo: getTempInfo(child),
        };
      }
      // @ts-ignore
      childs.push(child);
    }
  });
  return childs;
};

export const getNodeType = (node: PiNodeMetaData, orgInfo: ApiResponse.CurrentUser.OrgInfo) => {
  if (~node.n.indexOf('_')) {
    return 5;
  }

  if (typeof node.e._sys_node_type === 'number') {
    return node.e._sys_node_type;
  }
  return node.n === orgInfo.toBeSortId ? 2 : 0;
};

export const converViewGroupDataTypeWithMetadataToViewGroupDataType = (
  viewGroupDataTypeWithMetadata: ViewGroupDataTypeWithMetadata,
  nodeManager: PiNodeManager,
): ViewGroupDataType => {
  return Object.keys(viewGroupDataTypeWithMetadata).reduce((a, b) => {
    a[b] = {
      ...viewGroupDataTypeWithMetadata[b],
      list: viewGroupDataTypeWithMetadata[b].list.map((x) => nodeManager.findChildren(x.n)),
    };
    return a;
  }, {} as ViewGroupDataType);
};

// TODO: 和 newPinode 共用一份逻辑
export function isManage(
  prop: PiNodeMetaData['e'],
  role: OrgUser.OrgUserItem['role'],
  accountId: OrgUser.OrgUserItem['account_id'],
  _groupIdList: OrgUser.OrgUserItem['group_ids'],
) {
  // TODO: 空数组是否有额外业务判断
  const groupIdList = _groupIdList || [];
  if (role === 1 || role === 3) return true;
  if ('_sys_user_acl' in prop) {
    if (accountId in prop._sys_user_acl && prop._sys_user_acl[accountId][ACT.大纲管理]) return true;
    for (let i = 0; i < groupIdList.length; i++) {
      if (groupIdList[i] in prop._sys_user_acl && prop._sys_user_acl[groupIdList[i]][ACT.大纲管理])
        return true;
    }
  }
  if ('_sys_group_acl' in prop) {
    if (accountId in prop._sys_group_acl && prop._sys_group_acl[accountId][ACT.大纲管理])
      return true;
    for (let i = 0; i < groupIdList.length; i++) {
      if (
        groupIdList[i] in prop._sys_group_acl &&
        prop._sys_group_acl[groupIdList[i]][ACT.大纲管理]
      )
        return true;
    }
  }
  if ('_sys_parent_acl' in prop) {
    if (accountId in prop._sys_parent_acl && prop._sys_parent_acl[accountId][ACT.大纲管理])
      return true;
    for (let i = 0; i < groupIdList.length; i++) {
      if (
        groupIdList[i] in prop._sys_parent_acl &&
        prop._sys_parent_acl[groupIdList[i]][ACT.大纲管理]
      )
        return true;
    }
  }
  return false;
}

// TODO: 和 newPinode 共用一份逻辑
export function isEditable(
  nodeMetaData: PiNodeMetaData,
  role: OrgUser.OrgUserItem['role'],
  accountId: OrgUser.OrgUserItem['account_id'],
  _groupIdList: OrgUser.OrgUserItem['group_ids'],
) {
  const prop = nodeMetaData.e;
  // TODO: 空数组是否有额外业务判断
  const groupIdList = _groupIdList || [];

  // NOTE: 已经获取了 acl 判断
  // @ts-ignore
  if (typeof nodeMetaData.acl === 'number' && [0, 1].includes(nodeMetaData.acl)) return true;

  if (prop?._sys_readonly||prop?._sys_statusonly) return false;
  if (role === 1 || role === 3) return true;
  if (prop?._sys_na_readonly||prop?._sys_na_statusonly) return false;
  if (role === 4) return true;
  if (isManage(prop, role, accountId, groupIdList)) return true;

  if ('_sys_user_acl' in prop) {
    if (accountId in prop._sys_user_acl && prop._sys_user_acl[accountId][ACT.大纲编辑]) return true;
    for (let i = 0; i < groupIdList.length; i++) {
      if (groupIdList[i] in prop._sys_user_acl && prop._sys_user_acl[groupIdList[i]][ACT.大纲编辑])
        return true;
    }
  }

  if ('_sys_group_acl' in prop) {
    if (accountId in prop._sys_group_acl && prop._sys_group_acl[accountId][ACT.大纲编辑])
      return true;
    for (let i = 0; i < groupIdList.length; i++) {
      if (
        groupIdList[i] in prop._sys_group_acl &&
        prop._sys_group_acl[groupIdList[i]][ACT.大纲编辑]
      )
        return true;
    }
  }

  if ('_sys_parent_acl' in prop) {
    if (accountId in prop._sys_parent_acl && prop._sys_parent_acl[accountId][ACT.大纲编辑])
      return true;
    for (let i = 0; i < groupIdList.length; i++) {
      if (
        groupIdList[i] in prop._sys_parent_acl &&
        prop._sys_parent_acl[groupIdList[i]][ACT.大纲编辑]
      )
        return true;
    }
  }
  return false;
}

// 关于负责人提权
export function isEditableByOwner(
  nodeMetaData: PiNodeMetaData,
  role: OrgUser.OrgUserItem['role'],
  accountId: OrgUser.OrgUserItem['account_id'],
  _groupIdList: OrgUser.OrgUserItem['group_ids'],
) {
  const prop = nodeMetaData.e;
  let editable = isEditable(nodeMetaData, role, accountId, _groupIdList);
  if (editable) return true;

  // 负责人提权
  if (role === 5 || role === 7) {
    let principal = prop._sys_task_status?.prop?.[0] || [];
    let participant = prop._sys_task_status?.prop?.[1] || [];
    if (principal.indexOf(accountId) != -1 || participant.indexOf(accountId) != -1) return true;
  }

  return false;
}
